import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import FilterPanel from './components/FilterPanel';
import DataTable from './components/DataTable';
import ChartPanel from './components/ChartPanel';
import StatisticsPanel from './components/StatisticsPanel';
import QueryBuilder from './components/QueryBuilder';
import DataQualityIndicator from './components/DataQualityIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const HistoricalDataExplorer = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [activeView, setActiveView] = useState('table');
  const [savedQueries, setSavedQueries] = useState([]);
  
  const [filters, setFilters] = useState({
    startDate: '2024-01-01',
    endDate: '2024-12-31',
    contract: 'all',
    exchange: 'all',
    granularity: 'daily',
    dataType: 'all',
    minPrice: '',
    maxPrice: '',
    minVolume: '',
    maxVolume: '',
    quickFilter: ''
  });

  // Mock historical data
  const mockData = [
    {
      id: 1,
      date: '2024-09-20',
      contract: 'EUA Dec 2024',
      exchange: 'ICE',
      open: 68.45,
      high: 69.12,
      low: 67.89,
      close: 68.78,
      volume: 125000,
      openInterest: 890000,
      change: 0.33,
      changePercent: 0.48
    },
    {
      id: 2,
      date: '2024-09-19',
      contract: 'EUA Dec 2024',
      exchange: 'ICE',
      open: 68.12,
      high: 68.67,
      low: 67.45,
      close: 68.45,
      volume: 98000,
      openInterest: 885000,
      change: 0.33,
      changePercent: 0.48
    },
    {
      id: 3,
      date: '2024-09-18',
      contract: 'Brent Crude Oil',
      exchange: 'ICE',
      open: 74.25,
      high: 75.10,
      low: 73.80,
      close: 74.95,
      volume: 245000,
      openInterest: 1200000,
      change: 0.70,
      changePercent: 0.94
    },
    {
      id: 4,
      date: '2024-09-17',
      contract: 'WTI Crude Oil',
      exchange: 'NYMEX',
      open: 71.15,
      high: 71.85,
      low: 70.45,
      close: 71.60,
      volume: 180000,
      openInterest: 950000,
      change: 0.45,
      changePercent: 0.63
    },
    {
      id: 5,
      date: '2024-09-16',
      contract: 'EUA Dec 2025',
      exchange: 'ICE',
      open: 69.80,
      high: 70.25,
      low: 69.15,
      close: 69.95,
      volume: 75000,
      openInterest: 650000,
      change: 0.15,
      changePercent: 0.21
    },
    {
      id: 6,
      date: '2024-09-15',
      contract: 'Natural Gas',
      exchange: 'NYMEX',
      open: 2.45,
      high: 2.52,
      low: 2.38,
      close: 2.48,
      volume: 320000,
      openInterest: 1500000,
      change: 0.03,
      changePercent: 1.22
    },
    {
      id: 7,
      date: '2024-09-14',
      contract: 'Heating Oil',
      exchange: 'NYMEX',
      open: 2.15,
      high: 2.18,
      low: 2.12,
      close: 2.16,
      volume: 95000,
      openInterest: 420000,
      change: 0.01,
      changePercent: 0.46
    },
    {
      id: 8,
      date: '2024-09-13',
      contract: 'EUA Dec 2024',
      exchange: 'ICE',
      open: 67.90,
      high: 68.45,
      low: 67.25,
      close: 68.12,
      volume: 110000,
      openInterest: 880000,
      change: 0.22,
      changePercent: 0.32
    },
    {
      id: 9,
      date: '2024-09-12',
      contract: 'Brent Crude Oil',
      exchange: 'ICE',
      open: 73.80,
      high: 74.55,
      low: 73.20,
      close: 74.25,
      volume: 210000,
      openInterest: 1180000,
      change: 0.45,
      changePercent: 0.61
    },
    {
      id: 10,
      date: '2024-09-11',
      contract: 'WTI Crude Oil',
      exchange: 'NYMEX',
      open: 70.85,
      high: 71.40,
      low: 70.25,
      close: 71.15,
      volume: 165000,
      openInterest: 940000,
      change: 0.30,
      changePercent: 0.42
    }
  ];

  useEffect(() => {
    setData(mockData);
    setFilteredData(mockData);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, data]);

  const applyFilters = () => {
    setLoading(true);
    
    setTimeout(() => {
      let filtered = [...data];

      // Date range filter
      if (filters?.startDate) {
        filtered = filtered?.filter(item => item?.date >= filters?.startDate);
      }
      if (filters?.endDate) {
        filtered = filtered?.filter(item => item?.date <= filters?.endDate);
      }

      // Contract filter
      if (filters?.contract !== 'all') {
        filtered = filtered?.filter(item => 
          item?.contract?.toLowerCase()?.includes(filters?.contract?.toLowerCase())
        );
      }

      // Exchange filter
      if (filters?.exchange !== 'all') {
        filtered = filtered?.filter(item => item?.exchange === filters?.exchange);
      }

      // Price range filter
      if (filters?.minPrice) {
        filtered = filtered?.filter(item => item?.close >= parseFloat(filters?.minPrice));
      }
      if (filters?.maxPrice) {
        filtered = filtered?.filter(item => item?.close <= parseFloat(filters?.maxPrice));
      }

      // Volume range filter
      if (filters?.minVolume) {
        filtered = filtered?.filter(item => item?.volume >= parseInt(filters?.minVolume));
      }
      if (filters?.maxVolume) {
        filtered = filtered?.filter(item => item?.volume <= parseInt(filters?.maxVolume));
      }

      // Apply sorting
      if (sortConfig?.key) {
        filtered?.sort((a, b) => {
          const aVal = a?.[sortConfig?.key];
          const bVal = b?.[sortConfig?.key];
          
          if (sortConfig?.direction === 'asc') {
            return aVal > bVal ? 1 : -1;
          } else {
            return aVal < bVal ? 1 : -1;
          }
        });
      }

      setFilteredData(filtered);
      setLoading(false);
    }, 500);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    applyFilters();
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      contract: 'all',
      exchange: 'all',
      granularity: 'daily',
      dataType: 'all',
      minPrice: '',
      maxPrice: '',
      minVolume: '',
      maxVolume: '',
      quickFilter: ''
    });
  };

  const handleSort = (columnKey) => {
    setSortConfig(prevConfig => ({
      key: columnKey,
      direction: prevConfig?.key === columnKey && prevConfig?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleExport = (format) => {
    console.log(`Exporting data in ${format} format...`);
    // Mock export functionality
    const exportData = filteredData?.map(item => ({
      Date: item?.date,
      Contract: item?.contract,
      Exchange: item?.exchange,
      Open: item?.open,
      High: item?.high,
      Low: item?.low,
      Close: item?.close,
      Volume: item?.volume,
      'Open Interest': item?.openInterest,
      Change: item?.change,
      'Change %': item?.changePercent
    }));
    
    console.log('Export data:', exportData);
  };

  const handleSaveQuery = (query) => {
    setSavedQueries(prev => [...prev, query]);
    console.log('Query saved:', query);
  };

  const handleLoadQuery = (query) => {
    console.log('Loading query:', query);
    // Apply query conditions to filters
  };

  const viewOptions = [
    { key: 'table', label: 'Table View', icon: 'Table' },
    { key: 'chart', label: 'Chart View', icon: 'BarChart3' },
    { key: 'stats', label: 'Statistics', icon: 'PieChart' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto p-6 space-y-6">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Historical Data Explorer</h1>
              <p className="text-muted-foreground mt-1">
                Comprehensive access to archived EUA and Oil Futures market data with advanced analysis
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                iconName="RefreshCw"
                iconPosition="left"
                onClick={applyFilters}
                loading={loading}
              >
                Refresh Data
              </Button>
              <Button
                variant="default"
                iconName="Download"
                iconPosition="left"
                onClick={() => handleExport('csv')}
              >
                Export All
              </Button>
            </div>
          </div>

          {/* Filter Panel */}
          <FilterPanel
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
          />

          {/* View Toggle */}
          <div className="flex items-center gap-2 p-1 bg-muted rounded-lg w-fit">
            {viewOptions?.map((option) => (
              <button
                key={option?.key}
                onClick={() => setActiveView(option?.key)}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeView === option?.key
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={option?.icon} size={16} />
                {option?.label}
              </button>
            ))}
          </div>

          {/* Main Content Area */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            {/* Primary Content */}
            <div className="xl:col-span-3 space-y-6">
              {activeView === 'table' && (
                <DataTable
                  data={filteredData}
                  loading={loading}
                  onSort={handleSort}
                  sortConfig={sortConfig}
                  onExport={handleExport}
                />
              )}
              
              {activeView === 'chart' && (
                <ChartPanel
                  data={filteredData}
                  loading={loading}
                />
              )}
              
              {activeView === 'stats' && (
                <StatisticsPanel
                  data={filteredData}
                  loading={loading}
                />
              )}

              {/* Query Builder */}
              <QueryBuilder
                onSaveQuery={handleSaveQuery}
                onLoadQuery={handleLoadQuery}
                savedQueries={savedQueries}
              />
            </div>

            {/* Sidebar */}
            <div className="xl:col-span-1 space-y-6">
              {/* Data Quality Indicator */}
              <DataQualityIndicator
                data={filteredData}
                loading={loading}
              />

              {/* Quick Stats */}
              <div className="bg-card border border-border rounded-lg shadow-sm p-4">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Activity" size={20} />
                  Quick Stats
                </h3>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Total Records</span>
                    <span className="text-sm font-medium text-foreground">
                      {filteredData?.length?.toLocaleString()}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Date Range</span>
                    <span className="text-sm font-medium text-foreground">
                      {filteredData?.length > 0 ? 
                        `${filteredData?.[filteredData?.length - 1]?.date} - ${filteredData?.[0]?.date}` : 
                        'No data'
                      }
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Contracts</span>
                    <span className="text-sm font-medium text-foreground">
                      {new Set(filteredData.map(item => item.contract))?.size}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Exchanges</span>
                    <span className="text-sm font-medium text-foreground">
                      {new Set(filteredData.map(item => item.exchange))?.size}
                    </span>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-card border border-border rounded-lg shadow-sm p-4">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Clock" size={20} />
                  Recent Activity
                </h3>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-success rounded-full mt-2"></div>
                    <div className="flex-1">
                      <div className="text-sm text-foreground">Data refresh completed</div>
                      <div className="text-xs text-muted-foreground">2 minutes ago</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                    <div className="flex-1">
                      <div className="text-sm text-foreground">Filter applied: EUA contracts</div>
                      <div className="text-xs text-muted-foreground">5 minutes ago</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-warning rounded-full mt-2"></div>
                    <div className="flex-1">
                      <div className="text-sm text-foreground">Export completed: 1,250 records</div>
                      <div className="text-xs text-muted-foreground">15 minutes ago</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HistoricalDataExplorer;